import { useVendorNotPermitted } from '@melio/ap-domain';
import { AvatarWithDetails, AvatarWithDetailsProps, Group } from '@melio/penny';
import { Vendor, VendorEBillStatusEnum, VendorManagedByEnum } from '@melio/platform-api';
import { FeatureFlags, useFeature } from '@melio/platform-feature-flags';
import { useMelioIntl } from '@melio/platform-i18n';
import { useConfig } from '@melio/platform-provider';

import { getBadgeStatus } from '../EbillsCellBadge/EBillCellBadge';
import { LockedVendorIcon } from '../LockedVendorIcon/LockedVendorIcon';

export type VendorAvatarWithDetailsProps = {
  vendor: Vendor;
} & Partial<AvatarWithDetailsProps>;
export const VendorAvatarWithDetails = ({ vendor, ...props }: VendorAvatarWithDetailsProps) => {
  const { settings } = useConfig();
  const isVendorPermitted = useVendorNotPermitted(vendor);
  const [isSbbExclusiveUserView] = useFeature(FeatureFlags.SbbExclusiveUserView, false);
  const [isExternalVendorLogoEnabled] = useFeature<boolean>(FeatureFlags.ExternalVendorLogoEnabled, false);

  const { formatMessage } = useMelioIntl();
  if (!vendor) {
    return null;
  }

  const isVendorManagedByFiserv = vendor.managedBy === VendorManagedByEnum.Fiserv;
  const shouldDisplayLockIcon = isSbbExclusiveUserView && isVendorManagedByFiserv && isVendorPermitted;

  const companyName = vendor.name;
  const badgeProps =
    vendor.isManaged && settings.showCheckMarkIconForManagedVendor
      ? { badge: 'verified' as const, badgeColor: 'brand' as const }
      : undefined;
  const externalLogoUrl = isExternalVendorLogoEnabled ? vendor.externalLogoUrl : undefined;

  return (
    <Group variant="horizontal" spacing="xxs" alignItems="center">
      <AvatarWithDetails
        {...props}
        mainLabelProps={{
          label: shouldDisplayLockIcon
            ? formatMessage('widgets.vendorAvatarWithDetails.lockedVendor', {
                vendorName: companyName,
                lockIcon: <LockedVendorIcon />,
              })
            : companyName,
          badgeProps:
            vendor.eBillStatus !== VendorEBillStatusEnum.NotCapable
              ? {
                  type: 'status',
                  label: formatMessage('activities.payDashboard.eBillCellBadge.eBill'),
                  status: getBadgeStatus(vendor),
                }
              : undefined,
        }}
        avatarProps={{ name: companyName, src: externalLogoUrl, size: 'medium', ...badgeProps }}
      />
    </Group>
  );
};
